import React, { useState } from 'react';
import { ExternalLink, Play, X } from 'lucide-react';

/**
 * LinkPreview Component
 * Displays rich previews for URLs (YouTube, websites, images, etc.)
 * Similar to Facebook Messenger, Slack, Discord
 */
const LinkPreview = ({ preview, onRemove, isEditable = false }) => {
    const [showEmbed, setShowEmbed] = useState(false);

    if (!preview) return null;

    const isVideo = preview.video_type === 'youtube' || preview.video_type === 'vimeo';

    return (
        <div className="relative border dark:border-slate-700 border-slate-200 rounded-lg overflow-hidden dark:bg-slate-800/50 bg-slate-50 dark:hover:bg-slate-800 hover:bg-slate-100 transition-colors group">
            {isEditable && onRemove && (
                <button
                    onClick={onRemove}
                    className="absolute top-2 right-2 z-10 dark:bg-slate-900 bg-slate-200 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 dark:hover:bg-red-600"
                    title="Remove preview"
                >
                    <X className="w-4 h-4 dark:text-white text-slate-700 hover:text-white dark:hover:text-white" />
                </button>
            )}

            {/* Image/Video Thumbnail */}
            {preview.image && (
                <div className="relative">
                    {isVideo && !showEmbed ? (
                        <div
                            className="relative cursor-pointer"
                            onClick={() => setShowEmbed(true)}
                        >
                            <img
                                src={preview.image}
                                alt={preview.title}
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                                <div className="bg-red-600 rounded-full p-4 shadow-lg transform hover:scale-110 transition-transform">
                                    <Play className="w-8 h-8 text-white fill-white" />
                                </div>
                            </div>
                            <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white font-semibold">
                                {preview.video_type === 'youtube' ? 'YouTube' : 'Vimeo'}
                            </div>
                        </div>
                    ) : isVideo && showEmbed ? (
                        <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
                            <iframe
                                src={preview.embed_url}
                                className="absolute inset-0 w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={preview.title}
                            />
                        </div>
                    ) : (
                        <a
                            href={preview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                        >
                            <img
                                src={preview.image}
                                alt={preview.title}
                                className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </a>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="p-3">
                <div className="flex items-start gap-2">
                    {preview.favicon && (
                        <img
                            src={preview.favicon}
                            alt=""
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            onError={(e) => e.target.style.display = 'none'}
                        />
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">
                            {preview.site_name || (() => {
                                try {
                                    return new URL(preview.url).hostname;
                                } catch {
                                    return 'Website';
                                }
                            })()}
                        </p>
                        <a
                            href={preview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block hover:underline"
                        >
                            <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-1 line-clamp-2">
                                {preview.title}
                            </h4>
                        </a>
                        {preview.description && (
                            <p className="text-xs dark:text-slate-400 text-slate-600 line-clamp-2 mb-2">
                                {preview.description}
                            </p>
                        )}
                        <a
                            href={preview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs dark:text-yellow-400 text-yellow-600 hover:text-yellow-500 dark:hover:text-yellow-300 transition-colors"
                        >
                            <span className="truncate max-w-[200px]">
                                {preview.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            </span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LinkPreview;
